import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';
import { Text, Localizer, withText } from 'preact-i18n';
import { route } from 'preact-router';

import localUtil from '../../lib/util';
import DateTile from '../../components/gui/dateTile';
import {
    loadPublicCalendarData, getCalendarColor, courseColor, courseVisible,
} from '../../lib/calendarEventsLoader';
import { PublicEventExtras } from './publicEventExtras';

@withText(props => ({
    upcomingEventsText: <Text id='calendar.upcoming-events'>Upcoming Events</Text>,
    noEventsText: <Text id='calendar.no-upcoming-events'>No upcoming events</Text>,
    loadingText: <Text id='calendar.loading'>Loading events...</Text>,
    downloadIcalText: <Text id='calendar.download-ical'>Download calendar</Text>,
    subscribeText: <Text id='calendar.subscribe'>Subscribe to calendar</Text>,
    subscribeCopiedText: <Text id='calendar.subscribe-copied'>Link copied!</Text>,
}))
@observer
class PublicCalendarAll extends Component {
    constructor(props) {
        super(props);
        this.state = {
            events: [],
            courses: [],
            calendars: [],
            isLoading: true,
            error: null,
            showCopied: false,
            selectedCalendars: [], // Empty means all calendars, otherwise filter by these calendarIds
        };
    }

    loadEvents = async () => {
        const { customerId } = this.props;
        if (!customerId) {
            this.setState({ isLoading: false, error: 'No customer ID provided' });
            return;
        }

        const { events, courses, calendars, error } = await loadPublicCalendarData(customerId);
        if (error) {
            this.setState({ isLoading: false, error });
            return;
        }
        this.setState({ events, courses, calendars, isLoading: false });
    }

    componentDidMount() {
        this.loadEvents();
    }

    toggleCalendarFilter = (calendarId) => {
        const { selectedCalendars } = this.state;

        if (selectedCalendars.length === 0) {
            // Currently showing all - select only this one
            this.setState({ selectedCalendars: [calendarId] });
        } else if (selectedCalendars.includes(calendarId)) {
            // This calendar is selected
            if (selectedCalendars.length === 1) {
                // Only one selected, clicking it shows all
                this.setState({ selectedCalendars: [] });
            } else {
                // Multiple selected, remove this one
                this.setState({ selectedCalendars: selectedCalendars.filter(id => id !== calendarId) });
            }
        } else {
            // This calendar is not selected, add it
            this.setState({ selectedCalendars: [...selectedCalendars, calendarId] });
        }
    }

    isCalendarSelected = (calendarId) => {
        const { selectedCalendars } = this.state;
        return selectedCalendars.length === 0 || selectedCalendars.includes(calendarId);
    }

    getIcalUrl = (forDownload = false) => {
        const { customerId } = this.props;
        const apiServer = util.getApiServer();
        const url = `${apiServer}/api/calendarevents/ical/${customerId}`;
        return forDownload ? `${url}?download=1` : url;
    }

    getSubscribeUrl = () => {
        const { customerId } = this.props;
        const apiServer = util.getApiServer();
        // Replace http/https with webcal for calendar subscription
        const baseUrl = apiServer.replace(/^https?:/, 'webcal:');
        return `${baseUrl}/api/calendarevents/ical/${customerId}`;
    }

    downloadIcal = (e) => {
        e.preventDefault();
        window.open(this.getIcalUrl(true), '_blank');
    }

    copySubscribeLink = async (e) => {
        e.preventDefault();
        const url = this.getIcalUrl(false);
        try {
            await navigator.clipboard.writeText(url);
            this.setState({ showCopied: true });
            setTimeout(() => {
                this.setState({ showCopied: false });
            }, 2000);
        } catch (err) {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = url;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            this.setState({ showCopied: true });
            setTimeout(() => {
                this.setState({ showCopied: false });
            }, 2000);
        }
    }

    subscribeToCalendar = (e) => {
        e.preventDefault();
        window.location.href = this.getSubscribeUrl();
    }

    formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('nb-NO', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric',
        });
    }

    formatDateShort = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('nb-NO', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
        });
    }

    formatTime = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleTimeString('nb-NO', {
            hour: '2-digit',
            minute: '2-digit',
        });
    }

    handleEventClick = (event) => {
        const { appState } = this.props.stores;
        const { drawerLevel = 1 } = this.props;
        if (event.isConvention && event.conventionId) {
            appState.closeDrawer();
            route(`/convention/${event.conventionId}`);
        } else if (event.isCourse && event.courseId) {
            appState.closeDrawer();
            route(`/course/${event.courseId}`);
        } else if (event.isNsfCompetition && event.nsfCompetitionId) {
            // Terminliste stevne: detail drawer on top of this one (the page stays for sharing).
            appState.openDrawer('nsfCompetitionDetail', { id: event.nsfCompetitionId, height: appState.drawerHeightMediumLarge }, drawerLevel + 1);
        }
    }

    getCalendarColor = (calendarId) => getCalendarColor(this.state.calendars, calendarId);

    getCalendarName = (calendarId) => {
        const { calendars } = this.state;
        const calendar = calendars.find(c => c.calendarId === calendarId);
        return calendar?.name || '';
    }

    // Get ISO week number (Norwegian weeks start on Monday)
    getISOWeek = (date) => {
        const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
        const dayNum = d.getUTCDay() || 7;
        d.setUTCDate(d.getUTCDate() + 4 - dayNum);
        const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
        return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
    }

    // Get the Monday of the week for a given date
    getWeekStart = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
        return new Date(d.setDate(diff));
    }

    // Get the Sunday of the week for a given date
    getWeekEnd = (date) => {
        const weekStart = this.getWeekStart(date);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        return weekEnd;
    }

    formatWeekRange = (date) => {
        const weekStart = this.getWeekStart(date);
        const weekEnd = this.getWeekEnd(date);

        const formatOptions = { day: 'numeric', month: 'short' };
        const startStr = weekStart.toLocaleDateString('nb-NO', formatOptions);
        const endStr = weekEnd.toLocaleDateString('nb-NO', formatOptions);

        return `${startStr} - ${endStr}`;
    }

    groupEventsByWeek = (events) => {
        const grouped = {};

        events.forEach(event => {
            const date = new Date(event.start);
            const weekNum = this.getISOWeek(date);
            const year = date.getFullYear();
            // Handle week 1 that might belong to previous year's December
            const weekYear = (date.getMonth() === 11 && weekNum === 1) ? year + 1 : (date.getMonth() === 0 && weekNum >= 52) ? year - 1 : year;
            const weekKey = `${weekYear}-W${String(weekNum).padStart(2, '0')}`;
            const weekRange = this.formatWeekRange(date);

            if (!grouped[weekKey]) {
                grouped[weekKey] = {
                    name: `Uke ${weekNum}`,
                    weekRange,
                    year: weekYear,
                    events: [],
                };
            }
            grouped[weekKey].events.push(event);
        });

        return Object.entries(grouped)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([key, value]) => ({ key, ...value }));
    }

    render() {
        const { noEventsText, loadingText, downloadIcalText, subscribeText, subscribeCopiedText } = this.props;
        const { events, courses, calendars, isLoading, error, showCopied, selectedCalendars } = this.state;
        const { userStore } = this.props.stores;
        const { user = {} } = userStore;
        const darkmode = util.getNestedValue(user, 'settings.darkmode');

        // Filter events by selected calendars
        const filteredEvents = selectedCalendars.length === 0
            ? events
            : events.filter(e => selectedCalendars.includes(e.calendarId));

        // Expand course dates into individual calendar items. Courses belong to the club's
        // main calendar («Klubbkalenderen»): its colour, and its chip filters them.
        const now = new Date();
        const courseItems = [];
        const kursColor = courseColor(calendars);
        (courseVisible(calendars, selectedCalendars) ? courses : []).forEach(course => {
            const dates = course.dates || [];
            const participants = course.participants || [];
            const slots = course.slots || 0;
            dates.forEach((dateObj, dateIndex) => {
                // Only show dates where startTime is in the future
                if (dateObj.startTime && new Date(dateObj.startTime) >= now) {
                    courseItems.push({
                        isCourse: true,
                        courseId: course.id,
                        courseTitle: course.title,
                        dateMd5: dateObj.md5,
                        dateIndex,
                        start: dateObj.startTime,
                        end: dateObj.endTime,
                        title: dateObj.title || course.title,  // Use date title if available, fallback to course title
                        location: dateObj.location || course.place,
                        description: dateObj.description || course.ingress,
                        participantsCount: participants.length,
                        slots,
                    });
                }
            });
        });

        // Merge events and courses, then sort by start date
        const allItems = [...filteredEvents, ...courseItems].sort((a, b) => {
            const dateA = new Date(a.start);
            const dateB = new Date(b.start);
            return dateA - dateB;
        });

        if (isLoading) {
            return (
                <div class='p-3 text-center'>
                    <i class='fa-duotone fa-spinner fa-spin fa-2x' />
                    <p class='mt-2'>{loadingText}</p>
                </div>
            );
        }

        if (error) {
            return (
                <div class='p-3 text-center text-danger'>
                    <i class='fa-duotone fa-exclamation-triangle fa-2x' />
                    <p class='mt-2'>{error}</p>
                </div>
            );
        }

        if (allItems.length === 0) {
            return (
                <div class='p-3 text-center text-muted'>
                    <i class='fa-duotone fa-calendar-xmark fa-2x' />
                    <p class='mt-2'>{noEventsText}</p>
                </div>
            );
        }

        const groupedEvents = this.groupEventsByWeek(allItems);

        return (
            <div class='public-calendar-all p-3'>
                {/* iCal buttons */}
                <div class='d-flex flex-wrap gap-2 mb-3'>
                    <button
                        type='button'
                        class='btn btn-outline-primary btn-sm'
                        onClick={this.downloadIcal}
                    >
                        <i class='fa-solid fa-download me-1' />
                        {downloadIcalText}
                    </button>
                    <button
                        type='button'
                        class='btn btn-outline-secondary btn-sm'
                        onClick={this.subscribeToCalendar}
                    >
                        <i class='fa-solid fa-calendar-plus me-1' />
                        {subscribeText}
                    </button>
                    <button
                        type='button'
                        class={`btn btn-sm ${showCopied ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={this.copySubscribeLink}
                    >
                        <i class={`fa-solid ${showCopied ? 'fa-check' : 'fa-link'} me-1`} />
                        {showCopied ? subscribeCopiedText : <Text id='calendar.copy-link'>Copy link</Text>}
                    </button>
                </div>

                {/* Calendar filter legend */}
                {calendars.length > 1 && (
                    <div class='d-flex flex-wrap gap-2 mb-3'>
                        {calendars.map(cal => {
                            const isSelected = this.isCalendarSelected(cal.calendarId);
                            const color = this.getCalendarColor(cal.calendarId);
                            return (
                                <span
                                    key={cal.calendarId}
                                    class='badge px-2 py-1'
                                    style={`
                                        background-color: ${isSelected ? color : 'transparent'};
                                        color: ${isSelected ? 'white' : color};
                                        border: 2px solid ${color};
                                        cursor: pointer;
                                        opacity: ${isSelected ? 1 : 0.6};
                                    `}
                                    onClick={() => this.toggleCalendarFilter(cal.calendarId)}
                                >
                                    {cal.name}
                                </span>
                            );
                        })}
                    </div>
                )}

                {groupedEvents.map(week => (
                    <div key={week.key} class='week-section mb-3'>
                        <h6 class='text-muted mb-2 border-bottom pb-1 d-flex justify-content-between'>
                            <span>{week.name}</span>
                            <span class='small fw-normal'>{week.weekRange}</span>
                        </h6>
                        {week.events.map((event, idx) => {
                            const isClickable = event.isConvention || event.isCourse || event.isNsfCompetition;
                            const borderColor = event.isCourse ? kursColor : this.getCalendarColor(event.calendarId);
                            return (
                                <div
                                    key={event.id || event.dateMd5 || idx}
                                    class='d-flex justify-content-between align-items-start py-2'
                                    style={`border-left: 3px solid ${borderColor}; padding-left: 10px;${isClickable ? ' cursor: pointer;' : ''}`}
                                    onClick={isClickable ? () => this.handleEventClick(event) : undefined}
                                >
                                    <DateTile date={event.start} />
                                    <div class='flex-fill ms-3'>
                                        <div class={`fw-semibold${event.cancelled ? ' text-decoration-line-through text-body-secondary' : ''}`}>
                                            {event.isCourse && (
                                                <i class='fa-solid fa-graduation-cap me-2' style={`color: ${kursColor};`} />
                                            )}
                                            {event.title}
                                            <PublicEventExtras event={event} />
                                        </div>
                                        {event.isCourse && event.courseTitle && event.title !== event.courseTitle && (
                                            <div class='text-muted small'>
                                                <i class='fa-solid fa-book me-1' />
                                                {event.courseTitle}
                                            </div>
                                        )}
                                        {event.location && (
                                            <div class='text-muted small'>
                                                <i class='fa-solid fa-location-dot me-1' />
                                                {event.location}
                                            </div>
                                        )}
                                        {event.body && !event.isCourse && (
                                            <div class='text-muted small mt-1' style='white-space: pre-wrap;'>
                                                {event.body}
                                            </div>
                                        )}
                                        {event.isCourse && event.description && (
                                            <div class='text-muted small mt-1' style='white-space: pre-wrap; max-height: 60px; overflow: hidden;'>
                                                {event.description}
                                            </div>
                                        )}
                                        {event.isCourse && (
                                            <div class='text-muted small mt-1'>
                                                <i class='fa-solid fa-users me-1' />
                                                {event.participantsCount} / {event.slots > 0 ? event.slots : '∞'} <Text id='calendar.participants'>påmeldt</Text>
                                            </div>
                                        )}
                                    </div>
                                    <div class='text-end text-nowrap ms-2 small'>
                                        {event.isCourse && <div>
                                            <span class='badge' style={`background-color: ${kursColor};`}><Text id='calendar.course'>Kurs</Text></span>
                                        </div>}
                                        <div class='text-muted'>
                                            {this.formatTime(event.start)}
                                            {event.end && ` - ${this.formatTime(event.end)}`}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
        );
    }
}

export default PublicCalendarAll;
